import React, { Component } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import ReactTooltip from "react-tooltip";
import {
  AvatarWrapper,
  Avatar,
  CompanyName,
  CompanyNameWrapper,
  TableLineWrapper,
  TableLine,
  TableColumn,
  ToggleTrigger,
  TableLineExpandedContent,
  ExpandedLine,
  ExpandedColumn,
  ToolTip,
} from "../styles/league-table-styles";

export interface ILeagueTableLineProps {
  rank: number;
  recruiterId: string;
  name: string;
  avatar: string;
  origin: string;
  placements: number;
  engagements: number;
  interviewRate: number;
  originTooltip: string;
  intRateTooltip: string;
  placementsTooltip: string;
  engagementsTooltip: string;
  pdfExport?: boolean;
}

interface ILeagueTableLineState {
  isOpen: boolean;
}

export class LeagueTableLine extends Component<
  ILeagueTableLineProps,
  ILeagueTableLineState
> {
  static propTypes = {
    rank: PropTypes.number.isRequired,
    recruiterId: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    avatar: PropTypes.string.isRequired,
    origin: PropTypes.string.isRequired,
    placements: PropTypes.number.isRequired,
    engagements: PropTypes.number.isRequired,
    interviewRate: PropTypes.number.isRequired,
    originTooltip: PropTypes.string.isRequired,
    intRateTooltip: PropTypes.string.isRequired,
    placementsTooltip: PropTypes.string.isRequired,
    engagementsTooltip: PropTypes.string.isRequired,
    pdfExport: PropTypes.bool,
  };

  static defaultProps = {
    pdfExport: null,
  };

  state = {
    isOpen: false,
  };

  toggleLine = (): void => {
    const { isOpen } = this.state;
    this.setState({ isOpen: !isOpen });
  };

  render(): React.ReactNode {
    const {
      rank,
      recruiterId,
      name,
      avatar,
      origin,
      placements,
      engagements,
      interviewRate,
      originTooltip,
      intRateTooltip,
      placementsTooltip,
      engagementsTooltip,
      pdfExport,
    } = this.props;
    const { isOpen } = this.state;

    return (
      <TableLineWrapper pdfExport={pdfExport}>
        <TableLine onClick={this.toggleLine} pdfExport={pdfExport}>
          <TableColumn
            flex={1}
            pdfExport={pdfExport}
            rank
            data-test-id="league-table-table-line-rank"
          >
            {rank}.
          </TableColumn>
          <TableColumn flex={pdfExport ? 3 : 5} pdfExport={pdfExport}>
            {!pdfExport && (
              <AvatarWrapper>
                <Avatar
                  src={avatar}
                  alt="Avatar"
                  data-test-id="league-table-table-line-avatar"
                />
              </AvatarWrapper>
            )}
            <CompanyNameWrapper data-test-id="league-table-table-line-company-name">
              <CompanyName
                data-tip={`View ${name}'s profile`}
                data-for="league-table-tooltip"
                data-offset="{ 'top': -5 }"
                isOpen={isOpen}
                pdfExport={pdfExport}
                href={`/companies/${recruiterId}/profile?back_to=/employer/analytics`}
                alt={`View ${name}'s profile`}
              >
                {name}
              </CompanyName>
            </CompanyNameWrapper>
            <ToggleTrigger type="button" isOpen={isOpen}>
              <FontAwesomeIcon icon={faChevronDown} />
            </ToggleTrigger>
          </TableColumn>
          <TableColumn
            flex={2}
            pdfExport={pdfExport}
            noSmallDesktop
            data-test-id="league-table-table-line-origin"
          >
            {origin === "psl" ? "PSL" : origin}
          </TableColumn>
          <TableColumn
            flex={1}
            pdfExport={pdfExport}
            noSmallDesktop
            data-test-id="league-table-table-line-placements"
          >
            {placements}
          </TableColumn>
          <TableColumn
            flex={1}
            pdfExport={pdfExport}
            noSmallDesktop
            data-test-id="league-table-table-line-engagements"
          >
            {engagements}
          </TableColumn>
          <TableColumn
            flex={1}
            pdfExport={pdfExport}
            className="last"
            noSmallDesktop
            data-test-id="league-table-table-line-int-rate"
          >
            {interviewRate}%
          </TableColumn>
        </TableLine>
        {isOpen && (
          <TableLineExpandedContent>
            <ReactTooltip
              html
              place="top"
              type="dark"
              effect="solid"
              aria-haspopup="true"
              id="expanded-content-tooltip"
            />
            <ExpandedLine>
              <ExpandedColumn width={150}>Origin:</ExpandedColumn>
              <ExpandedColumn width={100}>
                {origin}
                <ToolTip
                  data-tip={originTooltip}
                  data-for="expanded-content-tooltip"
                >
                  <FontAwesomeIcon icon={faInfoCircle} />
                </ToolTip>
              </ExpandedColumn>
            </ExpandedLine>
            <ExpandedLine>
              <ExpandedColumn width={150}>Placements:</ExpandedColumn>
              <ExpandedColumn width={100}>
                {placements}
                <ToolTip
                  data-tip={placementsTooltip}
                  data-for="expanded-content-tooltip"
                >
                  <FontAwesomeIcon icon={faInfoCircle} />
                </ToolTip>
              </ExpandedColumn>
            </ExpandedLine>
            <ExpandedLine>
              <ExpandedColumn width={150}>Engagements:</ExpandedColumn>
              <ExpandedColumn width={100}>
                {engagements}
                <ToolTip
                  data-tip={engagementsTooltip}
                  data-for="expanded-content-tooltip"
                >
                  <FontAwesomeIcon icon={faInfoCircle} />
                </ToolTip>
              </ExpandedColumn>
            </ExpandedLine>
            <ExpandedLine>
              <ExpandedColumn width={150}>Interview Rate:</ExpandedColumn>
              <ExpandedColumn width={100}>
                {interviewRate}%
                <ToolTip
                  data-tip={intRateTooltip}
                  data-for="expanded-content-tooltip"
                >
                  <FontAwesomeIcon icon={faInfoCircle} />
                </ToolTip>
              </ExpandedColumn>
            </ExpandedLine>
          </TableLineExpandedContent>
        )}
      </TableLineWrapper>
    );
  }
}

export default LeagueTableLine;
